import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../routes/routeKeys";
import contact from "../../assets/svgs/account.svg";
import DownArrow from "../../assets/svgs/down-arrow.svg";
import Delete from "../../assets/svgs/Recycle Bin.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import { Skeleton } from "@chakra-ui/react";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialExclusiveFilterData } from "../../utils/initialData";
import ExclusiveArchieveFilter from "./ArchiveFilter/ExclusiveArchieveFilter";
import DataAsOfNotice from "../../components/DataAsOfNotice";
import Loader from "../../components/Loader";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const EXclusiveArchive = () => {
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [selectedItem, setSelectedItem] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState("initial_list_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [disableDelete, setDisableDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
  const [filterData, setFilterData] = useState(() => {
      const localFilterData = localStorage.getItem("filterData");
      return localFilterData ? JSON.parse(localFilterData) : initialExclusiveFilterData;
    });
  const queryParams = new URLSearchParams(location.search);

  // ==================== Debounced Search ====================
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  // Trigger API only when >= 3 chars or cleared
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  // URL sync with debounced search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  const listIds = listData?.map((el) => el.id);

  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`;

    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }
    const { count, start_count, end_count } = filters;
    if (count !== undefined) {
      params += `&count=${encodeURIComponent(count)}`;
    }
    if (start_count !== undefined) {
      params += `&start_count=${encodeURIComponent(start_count)}`;
    }
    if (end_count !== undefined) {
      params += `&end_count=${encodeURIComponent(end_count)}`;
    }
    if (sortDirection !== undefined && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id" && sortBy !== undefined) {
      params += `&sort_by=${sortBy}`;
    }

    localStorage.setItem("queryParams", params);

    return params;
  };

  const fetchData = (filters = filterData) => {
    const queryParams = buildQueryParams(filters);
    axios
      .get(`${BASE_URL}/archive-listing?${queryParams}&type=exclusives`, config)
      .then((res) => {
        const value = res?.data?.exclusivelist;
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
        setSelectedItem([]);
        setIsSelectAll(false);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => {
        setLoading(false);
        setSelectedId("");
      });
  };

  useEffect(() => {
    fetchData();
  }, [sortBy, sortDirection]);

  useEffect(() => {
    onSuccess();
  }, [currentPage, itemPerPage, search]);

    const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      fetchData(filters);
    } else {
      fetchData(filterData);
    }
  };

  const handleSelectCheck = (id) => {
    if (selectedItem.length === 0) {
      setSelectedItem([id]);
    } else {
      const index = selectedItem?.indexOf(id);
      if (index === -1) {
        setSelectedItem([...selectedItem, id]);
      } else {
        const filterData = selectedItem?.filter((el) => el !== id);
        setSelectedItem(filterData);
      }
    }
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleDelete = () => {
    const dataToSend = {
      action: "delete",
      type: "exclusive",
      ids: [selectedId],
    };
    setDisableDelete(true);

    axios
      .post(`${BASE_URL}/handle-bulkaction`, dataToSend, config)
      .then((res) => {
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getDisplayText = (el) => {
    if (el?.lead_type === 0 && el.portfolio !== 1) {
      if (!el?.link?.property_name) {
        return '';
      }
      return (
        <>
          <span>{el.link.property_name}</span>
          <br />
          <span>{el.link.street_address}</span>
          <br />
          <span>{el.link.city}, {el.link.state}</span>
        </>
      );
    }
    if (el?.lead_type === null) {
      if (!el?.link?.property_name) {
        return '';
      }
      return (
        <>
          <span>{el.link.property_name}</span>
          <br />
          <span>{el.link.street_address}</span>
          <br />
          <span>{el.link.city}, {el.link.state}</span>
        </>
      );
    }
    if (el?.lead_type === 1 && el.portfolio !== 1) {
      return el?.link?.property_type?.[0]?.type || '';
    }
    return el?.link?.name || '';
  };

  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">
          Exclusive Archive
          {selectedItem?.length > 0 && (
            <span className="body-L ml-2">
              ({selectedItem?.length} <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
            </span>
          )}
        </p>
      </div>

      <div className="md:flex justify-between items-center mb-4 mt-2 px-1">
        <div className="flex gap-4 items-center flex-wrap">
          <ExclusiveArchieveFilter
            filterData={filterData}
            onSetFilterData={(value) => {
              setFilterData(value);
            }}
            from="exclusivearchieve"
            onCallApiAgain={(filters) => onSuccess(filters)}
          />

          <div className="search-box contacts relative w-[250px]">
            <input
              type="text"
              className="body-S w-full"
              placeholder="Search name"
              value={searchInput}
              onChange={handleInputChange}
              style={{ border: "1px solid #D8D8D8", marginTop: "0px", width: "100%" }}
            />
          </div>
        </div>
      </div>

      <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} bg-white rounded-xl border border-gray-200 overflow-hidden mt-4 relative min-h-[400px]`}>
        {loading && (
          <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
            <Loader />
          </div>
        )}
        <div className="table-info">
            <table className="contact-table two-rows-static">
              <thead>
                <tr className="uppercase body-N dark-M">
                  <th>
                    <label className="container">
                      <input
                        type="checkbox"
                        checked={listData?.length === 0 ? false : isSelectAll}
                        onChange={(e) => {
                          setIsSelectAll(e.target.checked);
                          if (e.target.checked) {
                            setSelectedItem(listIds);
                          } else {
                            setSelectedItem([]);
                          }
                        }}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </th>


                  <th><p>Details</p></th>

                  <th>
                    <div className="table-header">
                      Initial List Date
                      <img
                        role="button"
                        src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("created_at");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Expired Date
                      <img
                        role="button"
                        src={sortBy !== "expired_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("expired_date");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Withdrawn Date
                      <img
                        role="button"
                        src={sortBy !== "withdrawn_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("withdrawn_date");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Closed Date
                      <img
                        role="button"
                        src={sortBy !== "closed_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("closed_date");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>


                  <th>
                    <div className="table-header">
                      Property Name
                      <img
                        role="button"
                        src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("property_name");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Status
                      <img
                        role="button"
                        src={sortBy !== "status_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("status_name");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      NOI
                      <img
                        role="button"
                        src={sortBy !== "anual_rent" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("anual_rent")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Asking Price
                      <img
                        role="button"
                        src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("asking_price")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Asking Cap Rate
                      <img
                        role="button"
                        src={sortBy !== "asking_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("asking_cap_rate")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>

                  <th>
                    <div className="table-header">
                      Listing Broker
                      <img
                        role="button"
                        src={sortBy !== "lead_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("lead_type")
                          setSortDirection(sortDirection === "desc" ? "asc" : "desc")
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>

                  <th><div className="table-header">Action</div></th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  [...Array(10)].map((_, idx) => (
                    <tr key={idx} className="dark-H body-N">
                      <td><Skeleton height="20px" width="20px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="60px" /></td>
                    </tr>
                  ))
                ) : listData?.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="body-N text-center mt-6 p-10 border-0">
                      No Exclusive Found
                    </td>
                  </tr>
                ) : (
                  listData?.map((el, idx) => (
                    <React.Fragment key={idx}>
                    <tr key={idx} className="dark-H body-N">
                      <td className="">
                        <label className="container">
                          <input type="checkbox" checked={selectedItem?.includes(el.id)} onChange={() => handleSelectCheck(el?.id)} />
                          <span className="checkmark"></span>
                        </label>
                      </td>

                      <td>
                        <img
                          role="button"
                          src={expandedRows[el.id] ? UpArrow : DownArrow}
                          alt="icon"
                          className="sidebar-icons"
                          onClick={() => toggleRowDetails(el.id)}
                        />
                      </td>
                      <td>{moment(el?.initial_list_date).tz('America/New_York').format("MM/DD/YY")}</td>

                      <td>
                        {el?.status_name === "Expired"
                          ? moment(el?.updated_at).tz("America/New_York").format("MM/DD/YY")
                          : ""}
                      </td>


                      <td>
                        {el?.status_name === "Withdrawn"
                          ? moment(el?.updated_at).tz("America/New_York").format("MM/DD/YY")
                          : ""}
                      </td>


                      <td>
                        {el?.status_name === "Closed"
                          ? moment(el?.updated_at).tz("America/New_York").format("MM/DD/YY")
                          : ""}
                      </td>


                      <td className="head-5 capitalize">
                        <Link
                          to={
                            el?.portfolio === 0
                              ? `/property/${el?.property_id}`
                              : el?.property?.id
                                ? `/property/${el?.property?.id}`
                                : el?.link?.owner_info_type === "contact"
                                  ? `/contact/${el?.link?.owner?.id}`
                                  : el?.link?.owner_info_type === "company"
                                    ? `/company/${el?.link?.owner?.id}`
                                    : "#"
                          }
                          onClick={(e) => {
                            e.preventDefault();

                            if (el?.portfolio === 0 && el?.property_id) {
                              navigate(ROUTES.PROPERTY_DETAILS.replace(":id", el.property_id));
                            } else if (el?.property?.id) {
                              navigate(ROUTES.PROPERTY_DETAILS.replace(":id", el.property.id));
                            } else if (el?.link?.owner_info_type === "contact" && el?.link?.owner?.id) {
                              navigate(ROUTES.CONTACT_DETAILS.replace(":id", el.link.owner.id));
                            } else if (el?.link?.owner_info_type === "company" && el?.link?.owner?.id) {
                              navigate(ROUTES.COMPANY_DETAILS.replace(":id", el.link.owner.id));
                            } else {
                              console.warn("Missing navigation data:", el);
                            }
                          }}
                        >
                          <div className="flex flex-col">
                            <span>{getDisplayText(el) || "—"}</span>
                          </div>

                        </Link>
                      </td>
                      <td className="text-center">
                        {el?.status_name}
                      </td>

                      <td className="text-center">
                        {el?.link?.noi_format ? `$${el.link?.noi_format.toLocaleString()}` : ""}
                      </td>

                      <td className="text-center">
                        {el?.link?.asking_price_format ? `$${el?.link?.asking_price_format.toLocaleString()}` : ""}
                      </td>

                      <td className="text-center">
                        {el?.link?.asking_cap_rate_format ? `${el.link?.asking_cap_rate_format.toLocaleString()}%` : ""}
                      </td>

                      <td className="head-5 green-H capitalize">
                        {el?.lead?.broker_details?.length > 0 ? (
                          el.lead.broker_details.map((broker) => (
                            <div key={broker.id}>
                              <Link
                                to={ROUTES.USER_DETAILS.replace(":id", broker.id)}
                                onClick={(e) => {
                                  e.preventDefault()
                                  navigate(ROUTES.USER_DETAILS.replace(":id", broker.id))
                                }}
                                rel="noopener noreferrer"
                                className="block text-gray-800 hover:text-green-700"
                              >
                                {broker.first_name} {broker.last_name}
                              </Link>
                            </div>
                          ))
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                      <td className="flex content-center">
                        <img
                          className="mx-4 sidebar-icons"
                          src={Delete}
                          alt="delete"
                          role="button"
                          onClick={() => {
                            setSelectedId(el?.id);
                            setShowDeleteModal(true);
                          }}
                        />
                      </td>
                    </tr>

                    {expandedRows[el.id] && (
                      <tr>
                        <td colSpan="11">
                          <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                            <div className="flex items-center justify-between mb-7">

                              <div>
                                <label className="dark-H head-4 block">Initial List Date</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span>
                                    {el?.initial_list_date ? moment(el?.initial_list_date).format("MM/DD/YYYY") : ""}
                                  </span>
                                </p>
                              </div>

                              <div>
                                <label className="dark-H head-4 block">Current List Date</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span>
                                    {el?.current_list_date ? moment(el?.current_list_date).format("MM/DD/YYYY") : ""}
                                  </span>
                                </p>
                              </div>

                              <div>
                                <label className="dark-H head-4 block">Listing Expiration Date</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span>
                                    {el?.expiration_date ? moment(el?.expiration_date).format("MM/DD/YYYY") : ""}
                                  </span>
                                </p>
                              </div>

                              <div>
                                <label className="dark-H head-4 block">Days Until Listing Expires</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span>
                                    {el?.days_until_listing_expiration}
                                  </span>
                                </p>
                              </div>

                              <div>
                                <label className="dark-H head-4 block">Days on Market</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span className=" green-H body-S">
                                    {el?.days_on_market}
                                  </span>
                                </p>
                              </div>

                            </div>

                            <div className="flex items-center justify-between">

                              <div>
                                <label className="dark-H head-4 block">Client</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span className=" green-H body-S">

                                    <Link
                                      to={el?.contact_id ? ROUTES.CONTACT_DETAILS.replace(":id", el.contact_id) : ROUTES.COMPANY_DETAILS.replace(":id", el.company_id)}
                                      rel="noopener noreferrer"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        if (el?.contact_id) {
                                          navigate(ROUTES.CONTACT_DETAILS.replace(":id", el.contact_id))
                                        } else {
                                          navigate(ROUTES.COMPANY_DETAILS.replace(":id", el.company_id))
                                        }
                                      }}
                                      className="flex items-center"
                                    >
                                      {el?.client && (
                                        <img src={contact || "/placeholder.svg"} alt="contact icon" className="sidebar-icons" />
                                      )}
                                      {el?.client}
                                    </Link>
                                  </span>
                                </p>
                              </div>

                              <div>
                                <label className="dark-H head-4 block">Company Commission</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span className=" green-H body-S">
                                    {el?.brokerage_commission ? ` ${el.brokerage_commission.toLocaleString()}%` : ""}
                                  </span>
                                </p>
                              </div>

                              <div>
                                <label className="dark-H head-4 block">Lease Type</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span>
                                    {el?.lease_type}
                                  </span>
                                </p>
                              </div>

                              <div>
                                <label className="dark-H head-4 block">Lease Term Remaining</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span>
                                    {el?.lease_team_remaining}
                                  </span>
                                </p>
                              </div>

                              <div>
                                <label className="dark-H head-4 block">Lease Expiration Date</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span>
                                    {el?.lease_expiration_date ? moment(el?.lease_expiration_date).format("MM/DD/YYYY") : ""}
                                  </span>
                                </p>
                              </div>

                              <div>
                                <label className="dark-H head-4 block">Gross Commission to Company</label>
                                <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                  <span>
                                    ${el?.gross_commission_company}
                                  </span>
                                </p>
                              </div>

                            </div>

                          </div>
                        </td>
                      </tr>
                    )}

                  </React.Fragment>
                ))
                )}
              </tbody>
            </table>
        </div>

        <ContactPagination
          from="archive"
          setSelectedItem={setSelectedItem}
          selectedItem={selectedItem}
          paginationData={paginationData}
          handleNext={handleNext}
          handlePrev={handlePrev}
          onSuccess={onSuccess}
          itemPerPage={itemPerPage}
          handleItemPerPageSelect={(value) => {
            setItemPerPage(value);
            setCurrentPage(1);
          }}
        />

        <div className="px-6 pb-2">
          <DataAsOfNotice />
        </div>
      </div>

      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />
    </BaseLayout>
  );
};

export default EXclusiveArchive;